import React from "react";
import AppShell from "@/components/AppShell";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import { MessageCircle, Phone, ExternalLink, Scale } from "lucide-react";
import { toast } from "sonner";

// Eid Law Firm — replace via env if you ever switch firm
const FIRM = {
  name: "Eid Law Firm",
  phoneDisplay: "+961 3 936 333",
  phoneE164: "9613936333", // E.164 without leading + for wa.me
};

const PREFILLS = {
  en: (name) => `Hello, I'm ${name || "a Law Pilot user"}. I would like to consult a lawyer about a matter under Lebanese law. Could we schedule a call?`,
  fr: (name) => `Bonjour, je suis ${name || "un utilisateur de Law Pilot"}. J'aimerais consulter un avocat concernant une question de droit libanais. Pouvons-nous convenir d'un rendez-vous ?`,
  ar: (name) => `مرحباً، أنا ${name || "أحد مستخدمي لو بايلوت"}. أرغب باستشارة محامٍ بشأن مسألة في القانون اللبناني. هل يمكننا تحديد موعد للاتصال؟`,
};

export default function LawFirm() {
  const { lang } = useLang();
  const { user } = useAuth();
  const message = (PREFILLS[lang] || PREFILLS.en)(user?.name || "");
  const waUrl = `https://wa.me/${FIRM.phoneE164}?text=${encodeURIComponent(message)}`;
  const telUrl = `tel:+${FIRM.phoneE164}`;

  // Open external/deep-link URLs in a way that escapes the Emergent preview webview
  // so the device's OS can hand the wa.me / tel: intent off to the right app.
  const openExternal = (url) => (e) => {
    e.preventDefault();
    // 1) Try a real new window first (works on desktop & most mobile browsers)
    let w = null;
    try { w = window.open(url, "_blank", "noopener,noreferrer"); } catch { /* ignore */ }
    if (w) return;
    // 2) Webview / popup-blocked → navigate the top-level frame so the OS intent fires
    try { window.top.location.href = url; return; } catch { /* ignore */ }
    // 3) Last resort
    window.location.href = url;
  };

  return (
    <AppShell>
      <div className="editorial-rule pt-4">
        <p className="text-xs tracking-[0.24em] uppercase text-[#C65D47]">{t(lang, "lawfirm.kicker")}</p>
        <h1 className="font-display text-4xl md:text-5xl mt-2" data-testid="lawfirm-title">{t(lang, "lawfirm.title")}</h1>
        <p className="mt-3 text-[#495057] max-w-2xl">{t(lang, "lawfirm.sub")}</p>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 bg-white border border-[#E9ECEF] p-8">
          <div className="flex items-center gap-3">
            <Scale className="w-6 h-6 text-[#C65D47]" />
            <h2 className="font-display text-3xl" data-testid="lawfirm-name">{FIRM.name}</h2>
          </div>
          <p className="mt-4 text-[#495057] leading-relaxed">{t(lang, "lawfirm.firm_desc")}</p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              onClick={openExternal(waUrl)}
              data-testid="lawfirm-whatsapp-btn"
              className="group inline-flex items-center justify-center gap-3 bg-[#25D366] text-white px-5 py-4 hover:bg-[#1FB855] transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="font-medium">{t(lang, "lawfirm.whatsapp_cta")}</span>
              <ExternalLink className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" />
            </a>
            <a
              href={telUrl}
              onClick={openExternal(telUrl)}
              data-testid="lawfirm-call-btn"
              className="group inline-flex items-center justify-center gap-3 border border-[#0A1128] text-[#0A1128] px-5 py-4 hover:bg-[#F8F9FA] transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span className="font-medium">{FIRM.phoneDisplay}</span>
            </a>
          </div>

          <button
            type="button"
            onClick={() => {
              try {
                navigator.clipboard.writeText(`+${FIRM.phoneE164}`);
                toast.success(t(lang, "lawfirm.copied"));
              } catch {
                toast.error(`+${FIRM.phoneE164}`);
              }
            }}
            data-testid="lawfirm-copy-btn"
            className="mt-3 text-xs text-[#495057] hover:text-[#0A1128] underline decoration-dotted underline-offset-4"
          >
            {t(lang, "lawfirm.copy_number")}
          </button>

          <div className="mt-8 border-t border-[#E9ECEF] pt-6">
            <p className="text-xs tracking-[0.24em] uppercase font-bold text-[#868E96] mb-3">{t(lang, "lawfirm.prefill_label")}</p>
            <p className="text-sm text-[#495057] bg-[#F8F9FA] border border-[#E9ECEF] p-4 leading-relaxed" data-testid="lawfirm-prefill-preview">
              {message}
            </p>
          </div>
        </div>

        <aside className="lg:col-span-5 bg-[#0A1128] text-white p-8">
          <p className="text-xs tracking-[0.24em] uppercase text-[#C65D47]">{t(lang, "lawfirm.when_kicker")}</p>
          <h3 className="font-display text-2xl mt-2">{t(lang, "lawfirm.when_title")}</h3>
          <ul className="mt-6 space-y-3 text-sm">
            {t(lang, "lawfirm.when_items").map((it, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`lawfirm-when-${i}`}>
                <span className="inline-block w-1.5 h-1.5 bg-[#C65D47] mt-2 shrink-0" />
                <span className="leading-relaxed text-white/85">{it}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs text-white/60">
            {t(lang, "lawfirm.note")}
          </p>
        </aside>
      </div>
    </AppShell>
  );
}
